import React from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  FileText,
  Upload,
  Library,
  Megaphone,
  Layers,
  LayoutTemplate,
  Lock,
  Sliders,
  Sparkles,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useAdAccount } from '@/contexts/AdAccountContext';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type NavSection = 'creatives' | 'launcher' | 'setup' | null;

interface NavLink {
  path: string;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  badge?: string;
}

interface ExpandablePanelProps {
  section: NavSection;
  onNavigate: (path: string) => void;
}

const sectionConfig: Record<string, { title: string; links: NavLink[]; showAdAccountSelector?: boolean }> = {
  creatives: {
    title: 'Creative Hub',
    showAdAccountSelector: true,
    links: [
      { path: '/creatives/upload', label: 'Upload Creatives', icon: Upload },
      { path: '/creatives/library', label: 'Creative Library', icon: Library },
    ],
  },
  launcher: {
    title: 'Ad Launcher',
    showAdAccountSelector: true,
    links: [
      { path: '/launcher/campaigns', label: 'Create Campaign', icon: Megaphone },
      { path: '/launcher/adsets', label: 'Create Ad Set', icon: Layers },
      { path: '/launcher/ad-sheets', label: 'Ad Sheets', icon: FileText },
      { path: '/launcher/templates', label: 'Templates', icon: LayoutTemplate },
    ],
  },
  setup: {
    title: 'Setup',
    showAdAccountSelector: true,
    links: [
      { path: '/setup/launch-settings', label: 'Launch Settings', icon: Sliders },
      { path: '/setup/enhancements', label: 'Enhancements', icon: Sparkles },
    ],
  },
};

export function ExpandablePanel({ section, onNavigate }: ExpandablePanelProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    selectedAdAccountId, 
    selectedAdAccountName, 
    setSelectedAdAccount, 
    adAccounts, 
    isLoading 
  } = useAdAccount();

  // Fetch setup_status for all user's ad accounts
  const { data: accountStatuses } = useQuery({
    queryKey: ['ad-account-statuses', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ad_accounts')
        .select('ad_account_id, setup_status')
        .eq('user_id', user!.id);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((row) => { map[row.ad_account_id] = row.setup_status; });
      return map;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
  });

  if (!section) return null;

  const config = sectionConfig[section];
  if (!config) return null;

  // Build a stable map so Select value always matches SelectItem value
  const accountById = new Map(adAccounts.map(a => [a.id, a]));
  const selectedAccount = selectedAdAccountId ? accountById.get(selectedAdAccountId) : null;

  const handleAccountChange = (value: string) => {
    const account = accountById.get(value);
    if (account) {
      setSelectedAdAccount(account.id, account.name);
    }
  };

  const getSetupStatus = (accountId: string): string => accountStatuses?.[accountId] ?? 'unconfigured';

  return (
    <div
      className={cn(
        'h-full w-[240px] bg-background border-r border-border shadow-lg z-40 shrink-0',
        'transform transition-transform duration-200 ease-out'
      )}
    >
      {/* Header */}
      <div className="h-16 flex items-center px-4 border-b border-border">
        <h2 className="font-semibold text-foreground">{config.title}</h2>
      </div>

      {/* Ad Account Selector (only for sections that need it) */}
      {config.showAdAccountSelector && (
        <div className="p-4 border-b border-border">
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Ad Account
          </label>
          <Select
            value={selectedAccount?.id ?? ''}
            onValueChange={handleAccountChange}
            disabled={isLoading || adAccounts.length === 0}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={isLoading ? 'Loading...' : 'Select account'}>
                {selectedAdAccountName ?? 'Select account'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              {adAccounts
                .filter((account) => getSetupStatus(account.id) === 'completed')
                .map((account) => (
                  <SelectItem key={account.id} value={account.id} textValue={account.name}>
                    <span>{account.name}</span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Navigation Links */}
      <nav className="p-2">
        {config.links.map((link) => {
          const isActive = location.pathname === link.path;
          
          return (
            <button
              key={link.path}
              onClick={() => !link.disabled && onNavigate(link.path)}
              disabled={link.disabled}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                'text-left',
                isActive
                  ? 'bg-secondary text-foreground font-medium'
                  : link.disabled
                    ? 'text-muted-foreground cursor-not-allowed opacity-60'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              {link.disabled ? (
                <Lock className="h-4 w-4 shrink-0" />
              ) : (
                <link.icon className="h-4 w-4 shrink-0" />
              )}
              <span className="flex-1">{link.label}</span>
              {link.badge && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {link.badge}
                </Badge>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
